import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Outlet, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { MotionConfig, AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import Index from "./pages/Index";
import Trading from "./pages/Trading";
import Consulting from "./pages/Consulting";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import NotFound from "./pages/NotFound";
import Platform from "./pages/Platform";
import Compliance from "./pages/Compliance";
import LcPolicy from "./pages/LcPolicy";
import AmsulUsa from "./pages/AmsulUsa";
import PalmProducts from "./pages/PalmProducts";
import BaseOil from "./pages/BaseOil";
import ConsumerPack from "./pages/ConsumerPack";
import Brands from "./pages/Brands";
import { QuotationCartProvider } from "@/hooks/use-quotation-cart";
import StoreLayout from "./pages/store/StoreLayout";
import StoreHome from "./pages/store/StoreHome";
import StoreCategory from "./pages/store/StoreCategory";
import StoreFamily from "./pages/store/StoreFamily";
import StoreCart from "./pages/store/StoreCart";
import StoreCartRequest from "./pages/store/StoreCartRequest";
import StoreCartRequestSuccess from "./pages/store/StoreCartRequestSuccess";
import StoreProduct from "./pages/store/StoreProduct";
import StoreLogin from "./pages/store/StoreLogin";
import StoreProfile from "./pages/store/StoreProfile";
import { RequireStoreAuth } from "./components/store/RequireStoreAuth";
import StoreQuotationHistory from "./pages/store/StoreQuotationHistory";
import StoreQuotationOrder from "./pages/store/StoreQuotationOrder";
import StoreCatalogShared from "./pages/store/StoreCatalogShared";
import StoreCatalogReclaim from "./pages/store/StoreCatalogReclaim";
import NewClientIdentification from "./pages/store/NewClientIdentification";
import AdminLayout from "./pages/store/admin/AdminLayout";
import AdminLogin from "./pages/store/admin/AdminLogin";
import AdminDashboard from "./pages/store/admin/AdminDashboard";
import AdminCategories from "./pages/store/admin/AdminCategories";
import AdminProductFamilies from "./pages/store/admin/AdminProductFamilies";
import AdminSourcingItems from "./pages/store/admin/AdminSourcingItems";
import AdminLeads from "./pages/store/admin/AdminLeads";
import AdminLeadDetail from "./pages/store/admin/AdminLeadDetail";
import AdminQuotationRequests from "./pages/store/admin/AdminQuotationRequests";
import AdminQuotationRequestNew from "./pages/store/admin/AdminQuotationRequestNew";
import AdminQuotationRequestDetail from "./pages/store/admin/AdminQuotationRequestDetail";
import AdminBrands from "./pages/store/admin/AdminBrands";
import AdminOrigins from "./pages/store/admin/AdminOrigins";
import AdminOriginPorts from "./pages/store/admin/AdminOriginPorts";
import AdminIncoterms from "./pages/store/admin/AdminIncoterms";
import AdminSuppliers from "./pages/store/admin/AdminSuppliers";
import AdminLoadingMethods from "./pages/store/admin/AdminLoadingMethods";
import AdminCatalogueConverter from "./pages/store/admin/AdminCatalogueConverter";
import AdminCompanySettings from "./pages/store/admin/AdminCompanySettings";
import AdminErrorReports from "./pages/store/admin/AdminErrorReports";
import AdminPseoConfig from "./pages/store/admin/AdminPseoConfig";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const queryClient = new QueryClient();

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [pathname]);

  return null;
};

const App = () => (
  <MotionConfig reducedMotion="user" transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <HelmetProvider>
          <BrowserRouter>
            <Toaster />
            <Sonner />
            <ErrorBoundary>
              <ScrollToTop />
              <AnimatePresence mode="wait">
                <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/trading" element={<Trading />} />
                <Route path="/trading/amsul-usa" element={<AmsulUsa />} />
                <Route path="/trading/palm-products" element={<PalmProducts />} />
                <Route path="/trading/base-oil" element={<BaseOil />} />
                <Route path="/consumer-pack" element={<ConsumerPack />} />
                <Route path="/brands" element={<Brands />} />
                <Route path="/platform" element={<Platform />} />
                <Route path="/compliance" element={<Compliance />} />
                <Route path="/lc-policy" element={<LcPolicy />} />
                <Route path="/consulting" element={<Consulting />} />
                <Route path="/about" element={<About />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/store" element={<QuotationCartProvider><StoreLayout /></QuotationCartProvider>}>
                  <Route index element={<StoreHome />} />
                  <Route path="category/:categorySlug" element={<StoreCategory />} />
                  <Route path="category/:categorySlug/:familySlug" element={<StoreFamily />} />
                  <Route path="product/:itemSlug" element={<StoreProduct />} />
                  <Route path="cart" element={<StoreCart />} />
                  <Route path="cart/request" element={<StoreCartRequest />} />
                  <Route path="cart/success" element={<StoreCartRequestSuccess />} />
                  <Route path="profile" element={<StoreProfile />} />
                  <Route path="quotations" element={<RequireStoreAuth><StoreQuotationHistory /></RequireStoreAuth>} />
                  <Route path="quotations/:id" element={<RequireStoreAuth><StoreQuotationOrder /></RequireStoreAuth>} />
                  <Route path="catalog/:token" element={<StoreCatalogShared />} />
                  <Route path="catalog/:token/reclaim" element={<StoreCatalogReclaim />} />
                  <Route path="login" element={<StoreLogin />} />
                  <Route path="newclientidentification" element={<NewClientIdentification />} />
                </Route>
                <Route path="/store/admin" element={<Outlet />}>
                  <Route path="login" element={<AdminLogin />} />
                  <Route element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="error-reports" element={<AdminErrorReports />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="product-families" element={<AdminProductFamilies />} />
                    <Route path="sourcing-items" element={<AdminSourcingItems />} />
                    <Route path="leads" element={<AdminLeads />} />
                    <Route path="leads/:id" element={<AdminLeadDetail />} />
                    <Route path="quotation-requests" element={<AdminQuotationRequests />} />
                    <Route path="quotation-requests/new" element={<AdminQuotationRequestNew />} />
                    <Route path="quotation-requests/:id" element={<AdminQuotationRequestDetail />} />
                    <Route path="brands" element={<AdminBrands />} />
                    <Route path="origins" element={<AdminOrigins />} />
                    <Route path="origin-ports" element={<AdminOriginPorts />} />
                    <Route path="incoterms" element={<AdminIncoterms />} />
                    <Route path="suppliers" element={<AdminSuppliers />} />
                    <Route path="loading-methods" element={<AdminLoadingMethods />} />
                    <Route path="company-settings" element={<AdminCompanySettings />} />
                    {/* Baja: PDF se genera desde la solicitud (generate-request-pdf). Redirigir a solicitudes. */}
                    <Route path="quotation-documents" element={<Navigate to="/store/admin/quotation-requests" replace />} />
                    <Route path="quotation-documents/:id" element={<Navigate to="/store/admin/quotation-requests" replace />} />
                    <Route path="pseo-config" element={<AdminPseoConfig />} />
                    <Route path="catalogue-converter" element={<AdminCatalogueConverter />} />
                  </Route>
                </Route>
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
                </Routes>
              </AnimatePresence>
            </ErrorBoundary>
          </BrowserRouter>
        </HelmetProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </MotionConfig>
);

export default App;
